import { validateFaceId, type FaceId } from "./heads.js";
import { validateHairId, type HairId } from "./hair.js";
import { validateBodyBuild, validateBodyShape, type BodyBuild, type BodyShape } from "./bodyShape.js";
/**
 * The RPG chassis: items, inventory, weapons, health — schema-first, like
 * ships.ts. The catalog and validation live here so the server can sanity-
 * check anything it relays or persists and every client agrees on item
 * stats. Rendering (held-weapon meshes, slot icons) is client-side, keyed
 * by item id: content should be catalog additions, not code.
 */

/** v49 adds three kinds. "gear" is wearable armour that occupies a StylePart equip
 *  slot; "vehicle" is a one-shot garage claim consumed into the collection ledger;
 *  "ship" is the same claim shape for an authored ship HULL (hangar tier). */
export type ItemKind =
  | "weapon" | "ammo" | "consumable" | "material" | "module"
  | "gear" | "vehicle" | "ship";
export type AmmoType = "light" | "heavy";
export type DamageSource = "fall" | "weapon" | "hazard";

/** Gameplay/loadout category. Visual hand placement is deliberately separate in
 * `WeaponHoldDef`: two `main` weapons may use completely different stances. */
export type WeaponClass = "main" | "sidearm" | "melee" | "shield";

/** The player's three modes are BODY constructs, not carried items: they mount on
 *  the forearms and are conjured/dissolved on a mode switch. NPCs still carry real
 *  guns, so this is a property of the def, not of the class. */
export type WeaponConstruct = "cannon" | "blade" | "shield";

/** Visual hold strategy, independent of combat/loadout class. A rocket launcher can
 * remain a `main` weapon while using `over-shoulder`; a heavy rotary gun can use
 * `underhand`; and any melee item that uses the blade animation set declares `sword`.
 * Adding a weapon should be a catalog change, not another avatar class check. */
export const WEAPON_STANCES = ["shoulder", "over-shoulder", "underhand", "sword"] as const;
export type WeaponStance = (typeof WEAPON_STANCES)[number];

export type WeaponVec3 = readonly [number, number, number];
export type WeaponQuat = readonly [number, number, number, number];

/** A hand socket authored in the weapon's canonical frame (barrel -Z, top +Y,
 * metres). `rotation` is the socket orientation in that frame as an [x,y,z,w]
 * quaternion; identity may be omitted. `slideTo` is an optional reachable point
 * farther back on the same guard, used when locomotion briefly pushes a support
 * grip beyond the arm's reach. */
export interface WeaponGripPoint {
  position: WeaponVec3;
  rotation?: WeaponQuat;
  slideTo?: WeaponVec3;
}

/** Rendering-only hand placement. `primary` is mandatory: even when it is the
 * canonical origin, spelling it out makes the weapon authoring contract visible.
 * Omit `support` for a one-handed weapon. */
export interface WeaponHoldDef {
  stance: WeaponStance;
  primary: WeaponGripPoint;
  support?: WeaponGripPoint;
}

/** Stable gameplay sockets in the canonical weapon frame (barrel -Z, top +Y,
 * metres). These live beside the model URL so firing never depends on the
 * asynchronous GLB load completing. `bore` is the rear point of the mechanical
 * barrel axis used by the pose/aim solvers. */
export interface WeaponFireSocket {
  muzzle: WeaponVec3;
  bore: WeaponVec3;
}

/** Close-range stats for a melee weapon (or, with no weapon, bare fists). */
export interface MeleeDef {
  /** Reach in metres from the player. */
  reach: number;
  /** Forward cone half-angle, radians. */
  arc: number;
  /** Damage per landed swing. */
  damage: number;
  /** Swing interval, ms (also the windup before the hit lands). */
  swingMs: number;
}

export type WeaponWingForm = 'pdw' | 'carbine' | 'sniper' | 'minigun' | 'launcher' | 'blade';
export interface WeaponDef {
  /** Silhouette assembled from the player's wings; mobility sets the piece budget. */
  wingForm?: WeaponWingForm;
  /** Which loadout slot this weapon lives in (1=main, 2=sidearm, 3=melee). */
  class: WeaponClass;
  damage: number;
  /** Rounds per minute. */
  rpm: number;
  projectileSpeed: number;
  ammo: AmmoType;
  /** Base cone half-angle, radians (bloom scales it while firing/moving). */
  spread: number;
  /** Magazine size; reloading pulls from the ammo pool. */
  mag: number;
  /** Reload time, seconds. */
  reload: number;
  // --- Per-weapon feel stats (2026-08-02). All OPTIONAL with engine defaults, so the
  //     constructs and older defs stay untouched; real carried guns should set them. ---
  /** Energy per shot (real guns; replaces the cannon-track cost). Default 3. */
  energyCost?: number;
  /** Max travelled path (m), including curved flight. Default speed × 1.4 seconds. */
  range?: number;
  /** Body/weapon recoil impulse per shot. Default 0.015; the camera stays steady. */
  recoil?: number;
  /** Spread added per sustained shot (bloom growth per shot, capped by bloomMax).
   *  Default 0.3 — lower = stays accurate longer under full-auto. */
  bloomPerShot?: number;
  /** Extra spread (rad) at full hip-fire (no ADS). Default 0.012. */
  hipSpread?: number;
  /** ADS magnification: how far the camera zooms while aiming (1 = no zoom;
   *  the FOV solves as 2·atan(tan(base/2)/adsZoom)). Default 1.3 ≈ the old
   *  flat 70°→56° aim tighten. Assault-class guns sit near the default; a
   *  marksman scope (Longshot) goes well above it. Look sensitivity scales
   *  down with the zoomed FOV automatically. */
  adsZoom?: number;
  /** Fraction of base damage lost at maximum range (0 flat, 1 fades to zero). Linear after falloffStart. */
  damageFalloff?: number;
  /** Distance in metres at which direct-hit damage starts falling. */
  falloffStart?: number;
  /** Projectile gravity multiplier: 0 is straight, 1 uses local gravity. */
  gravityScale?: number;
  /** Additional unique targets passed through; 0 stops on the first target. Solids always stop. */
  pierce?: number;
  /** Additional damage at the stopping point, independently of direct-hit damage. */
  explosiveDamage?: number;
  /** Blast radius in metres; cover blocks blast damage. */
  explosiveRadius?: number;
  /** Fraction lost at the radius (0 constant, 1 falls linearly to zero). */
  explosiveFalloff?: number;
  /** Mobility while deployed: 0 prevents flight, 1 leaves flight unimpaired. */
  mobility?: number;
  bloomMax?: number;
  /** Bloom recovered per second. */
  bloomRecovery?: number;
  /** Recoil spring recovery rate, per second. */
  recoilRecovery?: number;
  /** Seconds of held fire to fill the charge meter. Zero fires without charging.
   * Full charge sustains fire at RPM; it is not spent per shot. */
  chargeTime?: number;
  /** Seconds for a full charge to decay after releasing fire. Default 0.7. */
  chargeDecayTime?: number;
  /** Assembly/disassembly time in seconds. */
  deployTime?: number;
  stowTime?: number;
  /** Hit reaction strength multiplier (0 suppresses stagger). */
  impact?: number;
  /** Optional held-mesh override: a GLB under client/public (e.g. "/models/weapons/lancer.glb"),
   *  authored in the canonical hand frame (grip at origin, barrel -Z, top +Y, metres). When set,
   *  the avatar loads it onto hand_r via the existing weapon mount instead of building the
   *  procedural primitives. Absent → procedural (unchanged). A child node named "muzzle" marks
   *  the barrel tip; if absent the avatar synthesizes one at the -Z extent. */
  model?: string;
  /** Synchronous projectile origin and barrel axis for modeled weapons. The
   * GLB should contain matching `muzzle`/`bore` authoring nodes, but runtime
   * firing uses this stable socket from the moment the weapon is mounted. */
  fireSocket?: WeaponFireSocket;
  /** Explicit stance and hand sockets. Older/external definitions may omit this;
   * `weaponHold` supplies a class-compatible fallback. Catalog weapons should set it. */
  hold?: WeaponHoldDef;
  /** Present on melee-class weapons; gun fields above are then placeholders. */
  melee?: MeleeDef;
  /** Set on the player's three mode constructs — selects the forearm geometry the
   *  avatar builds, and marks the def as never-an-item (see PLAYER_CONSTRUCTS). */
  construct?: WeaponConstruct;
}

const DEFAULT_PRIMARY_GRIP: WeaponGripPoint = { position: [0, 0, 0] };
const DEFAULT_RIFLE_SUPPORT: WeaponGripPoint = {
  position: [-0.13, 0.02, -0.30],
  slideTo: [-0.10, -0.06, -0.15],
};

/** Backward-compatible resolver and the single place where legacy class semantics
 * become presentation defaults. New content should declare `hold` explicitly. */
export function weaponHold(weapon: Pick<WeaponDef, "class" | "hold">): WeaponHoldDef {
  if (weapon.hold) return weapon.hold;
  if (weapon.class === "melee") return { stance: "sword", primary: DEFAULT_PRIMARY_GRIP };
  if (weapon.class === "main") {
    return { stance: "shoulder", primary: DEFAULT_PRIMARY_GRIP, support: DEFAULT_RIFLE_SUPPORT };
  }
  return { stance: "shoulder", primary: DEFAULT_PRIMARY_GRIP };
}

/** Bare-handed (holstered) melee — the fisticuffs fallback when no weapon is held. */
export const FISTS_MELEE: MeleeDef = { reach: 2.4, arc: 1.0, damage: 9, swingMs: 420 };

/** RETIRED (user call 2026-08-10): nobody is issued a weapon any more. Rifles are
 *  DROPS — grantable, tradeable until worn, bind-on-equip, carried in the mainhand
 *  equip slot exactly like armor. The empty list keeps the legacy `standard`
 *  machinery compiling while validateInventory retires the field itself.
 *  (The light-blade construct rode this list; until it returns as a drop it is
 *  deliberately unobtainable.) */
export const STANDARD_WEAPONS: readonly string[] = [];

/**
 * Armor stats (v49) — "KIT, NOT CURVE". Four orthogonal axes; armor NEVER
 * raises damage (offense lives in the energy upgrade tracks). Every benefit is
 * paired against MASS, the universal cost; the caps below are the design
 * ceiling — a complete player knows it, and there is nothing past it to grind.
 * Craftsmanship (the settlement Q1–Q5 economy, later) reduces the COST side of
 * a piece, never raises the benefit side.
 */
export interface ArmorStats {
  /** Flat damage reduction share for HEALTH damage (under the shield).
   *  Total across the body caps at ARMOR_SOAK_CAP. */
  soak?: number;
  /** Environmental seal rating. Total caps at ARMOR_SEAL_MAX; each point cuts
   *  planet-hazard drain 20% — a full seal is hazard immunity. */
  seal?: number;
  /** Energy flow: each point is +5% on the ONE recharge stream — which fills
   *  the shield first, then action energy. */
  flow?: number;
  /** Mass: each point is +8% energy burn on boost/glide/hover — the cost axis. */
  mass?: number;
  /** Personal shield capacity (Halo-style: absorbs RAW damage before soak/hp).
   *  Total caps at ARMOR_SHIELD_CAP. No armor = no shield — the bare suit has
   *  health and a dodge. Bigger shields refill at the same rate, so they take
   *  longer to top up AND starve action-energy regen for longer: capacity is a
   *  tradeoff, not a win. */
  shield?: number;
}

/** The only combat numbers armor carries, and both are defense-only. */
export const ARMOR_SOAK_CAP = 0.3;
export const ARMOR_SEAL_MAX = 5;
export const ARMOR_SHIELD_CAP = 60;

/** Shield recharge: one base rate for every kit (×flow) — capacity changes how
 *  LONG a refill takes, never how fast the stream runs. */
export const SHIELD_REGEN_RATE = 9; // points/s at flow 0
/** Recharge holds off this long after any hit (shorter than hp's 8 s). */
export const SHIELD_REGEN_DELAY_MS = 4000;
/** Drawing action energy out of the shield is LOSSY: 2 shield → 1 energy.
 *  Cycling the pools is strictly worse than waiting; the draw's value is
 *  burst access, not economy. */
export const SHIELD_DRAW_RATIO = 2;
/** Action-energy regen multiplier while the shield is still refilling — the
 *  "shields first" half of the one-stream rule (client-side; the shield's own
 *  regen is the server's). */
export const SHIELD_CHARGE_ENERGY_GATE = 0.3;

/** The worn kit, aggregated: what the game actually applies. */
export interface ArmorProfile {
  soak: number;
  seal: number;
  flow: number;
  mass: number;
  /** Personal shield capacity (0 = no shield). */
  shieldMax: number;
  /** Shield recharge rate, points/s (the one stream × flow). */
  shieldRegen: number;
  /** Planet-hazard drain multiplier (0 = sealed suit, immune). */
  hazardScale: number;
  /** Energy regen multiplier from flow. */
  regenMul: number;
  /** Energy burn multiplier from mass (boost/glide/hover). */
  burnMul: number;
}

/** Aggregate the worn equipment into the applied profile. Pure; cheap enough
 *  to recompute on any inventory rev. */
export function armorProfile(equipped: Partial<Record<EquipSlot, string>>): ArmorProfile {
  let soak = 0, seal = 0, flow = 0, mass = 0, shield = 0;
  for (const id of Object.values(equipped)) {
    const s = (id ? ITEMS[id]?.gear?.stats : undefined) ?? undefined;
    if (!s) continue;
    soak += s.soak ?? 0;
    seal += s.seal ?? 0;
    flow += s.flow ?? 0;
    mass += s.mass ?? 0;
    shield += s.shield ?? 0;
  }
  soak = Math.min(ARMOR_SOAK_CAP, soak);
  seal = Math.min(ARMOR_SEAL_MAX, seal);
  shield = Math.min(ARMOR_SHIELD_CAP, shield);
  const regenMul = 1 + flow * 0.05;
  return {
    soak, seal, flow, mass,
    shieldMax: Math.round(shield),
    shieldRegen: SHIELD_REGEN_RATE * regenMul,
    hazardScale: Math.max(0, 1 - seal * 0.2),
    regenMul,
    burnMul: Math.max(0.5, 1 + mass * 0.08),
  };
}

/** Where a wearable sits, and which appearance it stamps into the wardrobe on first equip.
 *  `variant` must be a STYLE_VARIANTS entry for `slot` — equipping unlocks that look. */
export interface GearDef {
  slot: StylePart;
  variant: string;
  /** The piece's contribution to the worn ArmorProfile. */
  stats?: ArmorStats;
}

export interface ItemDef {
  id: string;
  name: string;
  kind: ItemKind;
  /** Max per stack. */
  stack: number;
  desc: string;
  /** Weapons only. */
  weapon?: WeaponDef;
  /** Ammo items: which pool they feed. */
  ammo?: AmmoType;
  /** Consumables: hp restored on use. */
  heal?: number;
  /** Consumables: seconds of hazard protection granted on use. */
  shield?: number;
  /** Modules: which ship perk installing this unlocks. */
  perk?: "boost" | "pulse" | "mining";
  /** Wearables (v49). Present ⇔ kind === "gear". */
  gear?: GearDef;
  /** Garage claim (v49): a VEHICLES id. Present ⇔ kind === "vehicle". */
  vehicle?: string;
  /** Hangar claim (v49): a ship-hull variant (ships.ts PART_VARIANTS.hull).
   *  Present ⇔ kind === "ship". */
  hull?: string;
  /** Soulbinding (v49). "equip" binds the stack when first worn/claimed (BoE);
   *  "pickup" binds it the moment it is granted (BoP). Absent = never binds. */
  bind?: "equip" | "pickup";
}

/** The three mode constructs. They are catalog defs (so the avatar, the HUD and the
 *  network keep resolving a held "weapon" by id, unchanged) but they are NOT items:
 *  never in an inventory, never dropped, never sold. `mag`/`reload` are dead fields —
 *  the modes spend ENERGY (see shared/src/combat.ts), which is why they read 0.
 *  Damage/rpm here are the LEVEL-0 baseline the server uses for PvP; the client
 *  scales them by the shooter's upgrades for PvE. */
export const PLAYER_CONSTRUCTS = ["arm-cannon", "light-blade", "energy-shield"] as const;

/** Display names for the gear slots, used to build item names ("Drifter Hood").
 *  ⚠ This — not STYLE_PARTS — is what gearSet() iterates. ITEMS is initialized
 *  ABOVE the STYLE_PARTS declaration, so reading it here is a TDZ crash at module
 *  load that typecheck cannot see. `Record<StylePart, string>` gives the same
 *  completeness guarantee: a new slot fails the build until it gets a noun. */
const GEAR_SLOT_NOUNS: Record<StylePart, string> = {
  head: "Hood", torso: "Vest and pack", arms: "Arm guards", legs: "Belted trousers", feet: "Boots",
};

/** One wearable per StylePart for a `variant`, keyed `gear-<variant>-<slot>`.
 *  A set is a data row, not code — see the drifter set in ITEMS below. */
function gearSet(
  variant: string,
  label: string,
  descs: Record<StylePart, string>,
  stats: Record<StylePart, ArmorStats>,
): Record<string, ItemDef> {
  const out: Record<string, ItemDef> = {};
  for (const slot of Object.keys(GEAR_SLOT_NOUNS) as StylePart[]) {
    const id = `gear-${variant}-${slot}`;
    out[id] = {
      id,
      name: `${label} ${GEAR_SLOT_NOUNS[slot]}`,
      kind: "gear",
      stack: 1,
      desc: descs[slot],
      gear: { slot, variant, stats: stats[slot] },
      bind: "equip",
    };
  }
  return out;
}

export const ITEMS: Record<string, ItemDef> = {
  "arm-cannon": {
    id: "arm-cannon",
    name: "Arm Cannon",
    kind: "weapon",
    stack: 1,
    desc: "Forearm-mounted bolt emitter. Draws on your energy — no magazine, no reload.",
    weapon: {
      wingForm: "pdw", class: "main", construct: "cannon", damage: 18, rpm: 150, projectileSpeed: 340,
      ammo: "heavy", spread: 0.006, mag: 0, reload: 0,
      hold: { stance: "shoulder", primary: { position: [0, 0, 0] } },
    },
  },
  "light-blade": {
    id: "light-blade",
    name: "Light Blade",
    kind: "weapon",
    stack: 1,
    desc: "A blade of hard light along the forearm. Each strike burns energy.",
    weapon: {
      wingForm: "blade", class: "melee", construct: "blade", damage: 0, rpm: 0, projectileSpeed: 0,
      ammo: "light", spread: 0, mag: 0, reload: 0,
      hold: { stance: "sword", primary: { position: [0, 0, 0] } },
      melee: { reach: 3.0, arc: 0.8, damage: 30, swingMs: 480 },
    },
  },
  "energy-shield": {
    id: "energy-shield",
    name: "Shield",
    kind: "weapon",
    stack: 1,
    desc: "An energy barrier on the off arm. Absorbs damage, and recharges you fastest.",
    weapon: {
      class: "shield", construct: "shield", damage: 0, rpm: 0, projectileSpeed: 0,
      ammo: "light", spread: 0, mag: 0, reload: 0,
      hold: { stance: "shoulder", primary: { position: [0, 0, 0] } },
    },
  },
  // The first REAL carried gun (not a construct): a two-handed rifle. `class:"main"` with no
  // `construct` is what routes the avatar onto the rifle stance (aimPrefix "r") and gives it a
  // real magazine + reload, unlike the energy modes. Procedural mesh for now (avatar.buildWeapon's
  // cls==="main" branch); a `model` GLB / modular parts slot in later without touching this row.
  // --- Rifles (2026-08-10): DROPS, like armor — tradeable until worn, bind on
  // equip, carried in the mainhand slot. Three shapes, three jobs. ---
  "rifle-mk1": {
    id: "rifle-mk1",
    name: "MK-1 Carbine",
    kind: "weapon",
    stack: 1,
    bind: "equip",
    desc: "The workhorse carbine. Forgiving, fast, short-legged.",
    weapon: {
      class: "main", damage: 22, rpm: 420, projectileSpeed: 420,
      ammo: "heavy", spread: 0.008, mag: 30, reload: 2.1,
      wingForm: "carbine", mobility: .85, damageFalloff: .4, falloffStart: 80, bloomMax: 1, bloomRecovery: 2.5, impact: 1,
      energyCost: 3, range: 220, recoil: 0.015, bloomPerShot: 0.3, hipSpread: 0.012, adsZoom: 1.35,
      model: "/models/weapons/rifle-mk1.glb",
      fireSocket: { muzzle: [0, 0.034, -0.55], bore: [0, 0.034, -0.30] },
      hold: {
        stance: "shoulder",
        primary: { position: [0, 0, 0] },
        support: { position: [-0.13, 0.02, -0.27], slideTo: [-0.10, -0.06, -0.15] },
      },
    },
  },
  // Second rifle: the STAT CONTRAST demo — a marksman rifle on the same chassis. Heavy hit,
  // slow cycle, laser-tight spread, long legs, thirsty on energy, real recoil.
  "rifle-longshot": {
    id: "rifle-longshot",
    name: "Longshot Rifle",
    kind: "weapon",
    stack: 1,
    bind: "equip",
    desc: "Marksman rifle. Slow, surgical, and expensive per shot — but it reaches out.",
    weapon: {
      class: "main", damage: 62, rpm: 80, projectileSpeed: 620,
      ammo: "heavy", spread: 0.0016, mag: 8, reload: 2.6,
      wingForm: "sniper", mobility: .55, pierce: 2, deployTime: .4, stowTime: .35, damageFalloff: .15, falloffStart: 350, impact: 1.8,
      energyCost: 11, range: 520, recoil: 0.05, bloomPerShot: 0.85, hipSpread: 0.03, adsZoom: 3.0,
      model: "/models/weapons/rifle-longshot.glb",
      fireSocket: { muzzle: [0, 0.031, -0.85], bore: [0, 0.031, -0.60] },
      hold: {
        stance: "shoulder",
        primary: { position: [0, 0, 0] },
        support: { position: [-0.13, 0.02, -0.30], slideTo: [-0.10, -0.06, -0.15] },
      },
    },
  },
  "rifle-vulcan": {
    id: "rifle-vulcan",
    name: "Vulcan PDW",
    kind: "weapon",
    stack: 1,
    bind: "equip",
    desc: "Stubby bullpup hose. Cheap per shot, wild past arm's length.",
    weapon: {
      class: "main", damage: 12, rpm: 780, projectileSpeed: 380,
      ammo: "heavy", spread: 0.014, mag: 48, reload: 1.8,
      wingForm: "pdw", mobility: 1, damageFalloff: .75, falloffStart: 35, bloomMax: 1.5, bloomRecovery: 3.5, deployTime: .18, stowTime: .18, impact: .6,
      energyCost: 2, range: 140, recoil: 0.008, bloomPerShot: 0.18, hipSpread: 0.016, adsZoom: 1.15,
      model: "/models/weapons/rifle-vulcan.glb",
      fireSocket: { muzzle: [0, 0.026, -0.34], bore: [0, 0.026, -0.20] },
      hold: {
        stance: "shoulder",
        primary: { position: [0, 0, 0] },
        support: { position: [-0.13, 0.02, -0.19], slideTo: [-0.10, -0.05, -0.10] },
      },
    },
  },
  "minigun-tempest": {
    id: "minigun-tempest",
    name: "Tempest Rotary Cannon",
    kind: "weapon",
    stack: 1,
    bind: "equip",
    desc: "A hip-braced six-barrel storm. Devours energy to saturate close approaches.",
    weapon: {
      class: "main", damage: 8, rpm: 1000, projectileSpeed: 440,
      ammo: "heavy", spread: 0.018, mag: 120, reload: 3.4,
      wingForm: "minigun", mobility: .18, chargeTime: .35, chargeDecayTime: .7, bloomMax: 2, bloomRecovery: 1.3, deployTime: .42, stowTime: .4, damageFalloff: .6, falloffStart: 55, recoilRecovery: 32, impact: .4,
      energyCost: 1.2, range: 170, recoil: 0.006, bloomPerShot: 0.12, hipSpread: 0.02, adsZoom: 1.08,
      model: "/models/weapons/minigun-tempest.glb",
      fireSocket: { muzzle: [0, 0.105, -0.73], bore: [0, 0.105, -0.45] },
      hold: {
        stance: "underhand",
        // Authored WRIST transforms corresponding to the GLB grip sockets.
        // They are offset by one palm depth from the visible bars so each bar
        // sits between thumb and fingers rather than underneath the wrist.
        primary: { position: [0, 0.17, 0.39] },
        support: { position: [-0.20, 0.34, -0.08] },
      },
    },
  },
  "launcher-ares": {
    id: "launcher-ares",
    name: "Ares Rocket Launcher",
    kind: "weapon",
    stack: 1,
    bind: "equip",
    desc: "An arcing plasma launcher. Grounded while deployed; eight-metre impact blast.",
    weapon: {
      class: "main", damage: 30, rpm: 48, projectileSpeed: 65,
      ammo: "heavy", spread: 0.0025, mag: 1, reload: 3.0,
      wingForm: "launcher", mobility: 0, gravityScale: 1, explosiveDamage: 80, explosiveRadius: 8, explosiveFalloff: 1, deployTime: .5, stowTime: .45, impact: 2.5,
      energyCost: 18, range: 340, recoil: 0.065, bloomPerShot: 1, hipSpread: 0.025, adsZoom: 1.22,
      model: "/models/weapons/launcher-ares.glb",
      fireSocket: { muzzle: [0, 0.22, -0.81], bore: [0, 0.22, -0.55] },
      hold: {
        stance: "over-shoulder",
        primary: { position: [0, 0, 0] },
        support: { position: [0, 0.048, -0.205], slideTo: [0, -0.035, -0.10] },
      },
    },
  },
  "ammo-light": {
    id: "ammo-light",
    name: "Light Cells",
    kind: "ammo",
    stack: 240,
    desc: "Energy cells for light sidearms.",
    ammo: "light",
  },
  "ammo-heavy": {
    id: "ammo-heavy",
    name: "Heavy Slugs",
    kind: "ammo",
    stack: 60,
    desc: "Dense slugs for rifle-class weapons.",
    ammo: "heavy",
  },
  medkit: {
    id: "medkit",
    name: "Medkit",
    kind: "consumable",
    stack: 5,
    desc: "Field trauma kit. Restores 50 integrity.",
    heal: 50,
  },
  "scrap-alloy": {
    id: "scrap-alloy",
    name: "Scrap Alloy",
    kind: "material",
    stack: 50,
    desc: "Salvaged hull plating. Trade or refine.",
  },
  "exotic-crystal": {
    id: "exotic-crystal",
    name: "Exotic Crystal",
    kind: "material",
    stack: 20,
    desc: "Hums faintly. Collectors in stable galaxies pay well.",
  },
  "ore-volatite": {
    id: "ore-volatite",
    name: "Volatite Ore",
    kind: "material",
    stack: 40,
    desc: "Unstable fuel ore. Veins run hot worlds — volcanic, desert.",
  },
  "ore-ferrite": {
    id: "ore-ferrite",
    name: "Ferrite Ore",
    kind: "material",
    stack: 40,
    desc: "Dense structural metal. Seams thread barren rock.",
  },
  ferriron: {
    id: "ferriron",
    name: "Ferriron",
    kind: "material",
    stack: 50,
    desc: "Refined structural metal used by settlement crafters.",
  },
  'garden-greens': {id:'garden-greens',name:'Garden Greens',kind:'material',stack:60,desc:'Fresh greens harvested from a town garden.'},
  'garden-roots': {id:'garden-roots',name:'Root Vegetables',kind:'material',stack:60,desc:'Root vegetables harvested from a town garden.'},
  'garden-beans': {id:'garden-beans',name:'Garden Beans',kind:'material',stack:60,desc:'Beans harvested from a town garden.'},
  wheat: {
    id: "wheat",
    name: "Wheat",
    kind: "material",
    stack: 60,
    desc: "A hardy grain grown in settlement Biogenerator plots.",
  },
  // --- Materia economy (v50): primary + secondary colors and the two rarer
  // raw metals. Ferrite already exists as ore-ferrite. ---
  "materia-red": {
    id: "materia-red",
    name: "Red Materia",
    kind: "material",
    stack: 60,
    desc: "Primary Materia attuned to force and heat. Synthesized, not mined.",
  },
  "materia-blue": {
    id: "materia-blue",
    name: "Blue Materia",
    kind: "material",
    stack: 60,
    desc: "Primary Materia attuned to energy and precision.",
  },
  "materia-yellow": {
    id: "materia-yellow",
    name: "Yellow Materia",
    kind: "material",
    stack: 60,
    desc: "Primary Materia attuned to motion and conduction.",
  },
  "materia-purple": {
    id: "materia-purple",
    name: "Purple Materia",
    kind: "material",
    stack: 60,
    desc: "Secondary Materia — Red and Blue combined. High-energy control.",
  },
  "materia-green": {
    id: "materia-green",
    name: "Green Materia",
    kind: "material",
    stack: 60,
    desc: "Secondary Materia — Blue and Yellow combined. Efficiency and recovery.",
  },
  "materia-orange": {
    id: "materia-orange",
    name: "Orange Materia",
    kind: "material",
    stack: 60,
    desc: "Secondary Materia — Red and Yellow combined. Impact and thrust.",
  },
  "ore-adamant": {
    id: "ore-adamant",
    name: "Adamant",
    kind: "material",
    stack: 40,
    desc: "Dense protective metal reclaimed from deep strata. Armor and heavy frames.",
  },
  "ore-runite": {
    id: "ore-runite",
    name: "Runite",
    kind: "material",
    stack: 40,
    desc: "Resonant conductive metal. Advanced weapons and high-energy systems.",
  },
  bread: {
    id: "bread",
    name: "Bread",
    kind: "material",
    stack: 30,
    desc: "Simple prepared food from the settlement Kitchen.",
  },
  "hearty-meal": {
    id: "hearty-meal",
    name: "Hearty Meal",
    kind: "material",
    stack: 30,
    desc: "A Chef's slow-cooked ration. Feeds one, and the town works happier for it.",
  },
  // --- Engineer upgrade packages (v50): a building level is INSTALLED, not
  // bought — the Engineer crafts the package, the upgrade consumes it. ---
  "upgrade-package-2": {
    id: "upgrade-package-2",
    name: "Established Works Package",
    kind: "material",
    stack: 5,
    desc: "An Engineer-built expansion kit. Installing it raises a building to Level 2.",
  },
  "upgrade-package-3": {
    id: "upgrade-package-3",
    name: "Expanded Works Package",
    kind: "material",
    stack: 5,
    desc: "A heavy Engineer-built expansion kit. Installing it raises a building to Level 3.",
  },
  "ore-cryonite": {
    id: "ore-cryonite",
    name: "Cryonite",
    kind: "material",
    stack: 40,
    desc: "Crystallized coolant. Blooms on ice worlds.",
  },
  "fuel-cell": {
    id: "fuel-cell",
    name: "Hyperlight Cell",
    kind: "material",
    stack: 12,
    desc: "One charge, one jump. Refined from volatite.",
  },
  "shield-pack": {
    id: "shield-pack",
    name: "Aegis Pack",
    kind: "consumable",
    stack: 6,
    desc: "120 seconds of environmental shielding. Heat, cold, vacuum.",
    shield: 120,
  },
  "module-boost": {
    id: "module-boost",
    name: "Afterburner Module",
    kind: "module",
    stack: 1,
    desc: "Install: boost runs 30% harder.",
    perk: "boost",
  },
  "module-pulse": {
    id: "module-pulse",
    name: "Pulse Capacitor",
    kind: "module",
    stack: 1,
    desc: "Install: pulse drive winds up faster and tops out higher.",
    perk: "pulse",
  },
  "module-mining": {
    id: "module-mining",
    name: "Resonance Amp",
    kind: "module",
    stack: 1,
    desc: "Install: mining cycles run 40% faster.",
    perk: "mining",
  },
  // --- Wearable gear (v49) ---
  // The drifter set as real items: the GLBs, the STYLE_VARIANTS entries and the
  // unlock tokens all already existed, so this only gives the wardrobe something to
  // BE. bind:"equip" = soulbound the first time it is worn (BoE), the WoW rule.
  // A new set is 8 more rows here + a COSMETICS entry client-side; nothing else.
  // The drifter kit: a balanced light expedition rig. Worn COMPLETE it reads
  // soak 0.15 / seal 4 / flow 3 / mass 4 (hazard drain ×0.2, regen ×1.15,
  // burn ×1.32) — survives a tier-1 world at the price of a heavier boost.
  // Mixing pieces is the point: torso+legs for a fight, arms+feet for
  // a settlement run.
  ...gearSet("drifter", "Drifter", {
    head: "A scavenger's hood, cut for a rebreather that is no longer attached.",
    torso: "Layered ballistic weave with a long-haul pack, straps cinched for running.",
    arms: "Asymmetric pauldrons and split-finger work gloves.",
    legs: "Reinforced trousers with an attached tool belt, knees rebuilt twice over.",
    feet: "Hard-wearing boots. The tread is a memory.",
  }, {
    head: { soak: 0.02, seal: 1, shield: 5 },
    torso: { soak: 0.05, seal: 2, mass: 2, shield: 18 },
    arms: { soak: 0.03, mass: 1, shield: 8, flow: 1 },
    legs: { soak: 0.04, seal: 1, mass: 1, shield: 4, flow: 1 },
    feet: { soak: 0.01, flow: 1 },
  }),
  // --- Garage keycards (v49) ---
  // Using one adds the chassis to your garage PERMANENTLY and destroys the card.
  // Deliberately UNBOUND: a card for a vehicle you already own is refused rather
  // than consumed, so the duplicate stays sellable (WoW's known-mount behaviour).
  // `vehicle` must name a VEHICLES id — shared/test/equipment.test.mts asserts it.
  "keycard-scout-speeder": {
    id: "keycard-scout-speeder",
    name: "Scout Speeder Keycard",
    kind: "vehicle",
    stack: 1,
    desc: "Registers a SCOUT SPEEDER to your garage. Agile, forgiving, standard issue.",
    vehicle: "scout-speeder",
  },
  "keycard-interceptor-speeder": {
    id: "keycard-interceptor-speeder",
    name: "Interceptor Keycard",
    kind: "vehicle",
    stack: 1,
    desc: "Registers an INTERCEPTOR to your garage. Faster, twitchier, less forgiving.",
    vehicle: "interceptor-speeder",
  },
  "keycard-hauler-speeder": {
    id: "keycard-hauler-speeder",
    name: "Heavy Hauler Keycard",
    kind: "vehicle",
    stack: 1,
    desc: "Registers a HEAVY HAULER to your garage. Slow, stable, four seats.",
    vehicle: "hauler-speeder",
  },
  "keycard-hover-bike": {
    id: "keycard-hover-bike",
    name: "Hover Bike Keycard",
    kind: "vehicle",
    stack: 1,
    desc: "Registers a HOVER BIKE to your garage. Straight lines, very fast ones.",
    vehicle: "hover-bike",
  },
  "keycard-m66-stingray": {
    id: "keycard-m66-stingray",
    name: "M-66 Stingray Keycard",
    kind: "vehicle",
    stack: 1,
    desc: "Registers an M-66 STINGRAY to your garage. Powered flight; prone pilot.",
    vehicle: "m66-stingray",
  },
  "keycard-banshee": {
    id: "keycard-banshee",
    name: "Banshee Keycard",
    kind: "vehicle",
    stack: 1,
    desc: "Registers a BANSHEE to your garage. Single seat, and it climbs like nothing else.",
    vehicle: "banshee",
  },
  // --- Hangar registrations (v49) — the ship-hull tier of the same claim. ---
  "keycard-lightspeed": {
    id: "keycard-lightspeed",
    name: "Lightspeed Registration",
    kind: "ship",
    stack: 1,
    desc: "Registers a LIGHTSPEED interceptor to your hangar. Fast, long-legged, and it never forgets a landing spot.",
    hull: "lightspeed",
  },
};

// --- Colored ingots (v50): eighteen stable identities. The id carries
// (color, metal); the GRADE rides the stack's quality stamp (§4.3). ---
export const INGOT_METALS = ["ferrite", "adamant", "runite"] as const;
export const INGOT_COLORS = ["red", "blue", "yellow", "purple", "green", "orange"] as const;
export type IngotMetal = (typeof INGOT_METALS)[number];
export type IngotColor = (typeof INGOT_COLORS)[number];

export function coloredIngotId(color: IngotColor, metal: IngotMetal): string {
  return `ingot-${color}-${metal}`;
}

{
  const title = (word: string): string => word[0]!.toUpperCase() + word.slice(1);
  for (const metal of INGOT_METALS) {
    for (const color of INGOT_COLORS) {
      const id = coloredIngotId(color, metal);
      ITEMS[id] = {
        id,
        name: `${title(color)}-${title(metal)} Ingot`,
        kind: "material",
        stack: 40,
        desc: `${title(metal)} infused with ${title(color)} Materia — a stable crafting ingot.`,
      };
    }
  }
}

// --- Crafting ---

export interface Recipe {
  out: ItemStack;
  in: ItemStack[];
}

export const RECIPES: Recipe[] = [
  { out: { id: "fuel-cell", n: 1 }, in: [{ id: "ore-volatite", n: 3 }, { id: "ore-ferrite", n: 1 }] },
  { out: { id: "medkit", n: 1 }, in: [{ id: "ore-cryonite", n: 2 }, { id: "scrap-alloy", n: 1 }] },
  { out: { id: "shield-pack", n: 1 }, in: [{ id: "ore-cryonite", n: 3 }, { id: "exotic-crystal", n: 1 }] },
  { out: { id: "ammo-light", n: 60 }, in: [{ id: "ore-ferrite", n: 2 }] },
  { out: { id: "ammo-heavy", n: 15 }, in: [{ id: "ore-ferrite", n: 3 }] },
  { out: { id: "module-boost", n: 1 }, in: [{ id: "ore-ferrite", n: 6 }, { id: "exotic-crystal", n: 2 }] },
  { out: { id: "module-pulse", n: 1 }, in: [{ id: "ore-volatite", n: 6 }, { id: "exotic-crystal", n: 2 }] },
  { out: { id: "module-mining", n: 1 }, in: [{ id: "ore-ferrite", n: 4 }, { id: "ore-cryonite", n: 4 }] },
];

/** How many of `id` the inventory holds (across stacks, bound included). */
export function countItem(inv: InventoryState, id: string): number {
  let n = 0;
  for (const s of inv.slots) if (s?.id === id) n += s.n;
  return n;
}

/**
 * The TRADEABLE count: unbound stacks only (v49). Soulbound copies never change
 * hands — every transfer site (player trade, market sell, board listing,
 * settlement deposit) gates with THIS, and consumeItem's unbound-first spending
 * order then guarantees the bound stacks are never the ones consumed.
 */
export function countUnbound(inv: InventoryState, id: string): number {
  let n = 0;
  for (const s of inv.slots) if (s?.id === id && s.b !== 1) n += s.n;
  return n;
}

/** True if every ingredient is on hand. */
export function canCraft(inv: InventoryState, r: Recipe): boolean {
  return r.in.every((ing) => countItem(inv, ing.id) >= ing.n);
}

/** Remove `n` of `id` from wherever it stacks. False (no change) if short.
 *  Spends UNBOUND stacks first so a soulbound copy survives where it can. */
export function consumeItem(inv: InventoryState, id: string, n: number): boolean {
  if (countItem(inv, id) < n) return false;
  let left = n;
  const order = [...inv.slots.keys()].sort(
    (a, b) => Number(stackIsBound(inv.slots[a])) - Number(stackIsBound(inv.slots[b])),
  );
  for (const i of order) {
    if (left <= 0) break;
    const s = inv.slots[i];
    if (!s || s.id !== id) continue;
    const take = Math.min(left, s.n);
    s.n -= take;
    left -= take;
    if (s.n === 0) {
      inv.slots[i] = null;
      if (inv.primary === i) inv.primary = null;
      if (inv.secondary === i) inv.secondary = null;
    }
  }
  return true;
}

/** Craft a recipe: consume ingredients, add the output. False if short. */
export function craft(inv: InventoryState, r: Recipe): boolean {
  if (!canCraft(inv, r)) return false;
  for (const ing of r.in) consumeItem(inv, ing.id, ing.n);
  addItem(inv, r.out.id, r.out.n);
  return true;
}

export const INV_SLOTS = 24;

// --- Character appearance (the ship-builder philosophy: variant parts +
// per-part colors, validated here so every client renders the same pilot) ---

// BASE BODY models. "male" is the sculpted skinned base (client/public/models/male/, built by
// client/test/build-male.mts from the raw pack in client/model-library/male/); "female" is the
// sculpted female base (client/public/models/female/, built by client/test/build-female.mts —
// its OWN rest skeleton, femaleRig.ts); "block" is the holographic body (the male mesh drawn
// with the holo material). Future bodies (alien, …) = new asset dir + an id here + a client
// BODY_MODELS entry (+ a BODY_RIGS entry if the anatomy differs).
export const BODY_IDS = ["male", "female", "block"] as const;
export type BodyId = (typeof BODY_IDS)[number];

/** Display names for the character creator's body select. */
export const BODY_LABELS: Record<BodyId, string> = {
  male: "EXPLORER",
  female: "WAYFARER",
  block: "HOLOGRAPHIC",
};

/** Five wearable regions. Belts belong to legs; packs to torso; shoulder gear to arms. */
export const STYLE_PARTS = ["head", "torso", "arms", "legs", "feet"] as const;
export type StylePart = (typeof STYLE_PARTS)[number];
export const STYLE_VARIANTS: Record<StylePart, readonly string[]> = {
  head: ["none", "drifter"], torso: ["none", "rookie", "drifter"],
  arms: ["none", "rookie", "drifter"], legs: ["none", "rookie", "drifter"], feet: ["none", "rookie", "drifter"],
};
/** Read-only compatibility at persistence boundaries; new writes use the five names. */
const LEGACY_STYLE_PARTS: Readonly<Record<string, StylePart>> = {
  chest: "torso", back: "torso", shoulder: "arms", hands: "arms", waist: "legs", pants: "legs", shoe: "feet",
};
export function canonicalStylePart(value: string): StylePart | null {
  return (STYLE_PARTS as readonly string[]).includes(value) ? value as StylePart : (Object.hasOwn(LEGACY_STYLE_PARTS, value) ? LEGACY_STYLE_PARTS[value] : null);
}
export function canonicalWardrobeToken(token: string): string {
  const separator = token.indexOf(":");
  const part = canonicalStylePart(token.slice(0, separator));
  return separator > 0 && part ? `${part}${token.slice(separator)}` : token;
}
/** Old attachment items become their combined wearable. Worn attachments merge into one set piece. */
export function canonicalItemId(id: string): string {
  const match = /^gear-drifter-(chest|back|shoulder|hands|waist|pants|shoe)$/.exec(id);
  return match ? `gear-drifter-${LEGACY_STYLE_PARTS[match[1]]}` : id;
}

// Keep old persisted item references readable without listing duplicate legacy
// pieces in shops, loot pools or the five-slot item catalogue.
for (const oldSlot of Object.keys(LEGACY_STYLE_PARTS)) {
  const id = `gear-drifter-${oldSlot}`;
  Object.defineProperty(ITEMS, id, { value: ITEMS[canonicalItemId(id)], enumerable: false });
}

// --- Equipment slots + the collection ledger (v49) ---
//
// THE SPLIT: CharacterStyle is what you LOOK like (transmog — broadcast to the room,
// persisted on the character record). InventoryState.equipped is what you are WEARING
// (stats — server state, out of the bag). Transmog is the two disagreeing.

/** Equip slots: the five wearable regions plus the weapon hands. Worn items live here
 *  instead of in a bag slot, which is what gives the bag space back. */
export const EQUIP_SLOTS = [...STYLE_PARTS, "mainhand", "offhand"] as const;
export type EquipSlot = (typeof EQUIP_SLOTS)[number];

export function isEquipSlot(v: unknown): v is EquipSlot {
  return typeof v === "string" && (EQUIP_SLOTS as readonly string[]).includes(v);
}

/** Wardrobe token for an appearance. Matches the grammar CACHE_UNLOCK_POOL already emits,
 *  so every unlock earned from a cache clear is already a wardrobe entry. */
export function gearUnlockToken(slot: StylePart, variant: string): string {
  return `${slot}:${variant}`;
}

/** Garage token for a vehicle. A separate namespace in the SAME ledger
 *  (CharacterRecordV1.unlocks) — see shared/src/creation.ts sanitizeUnlocks. */
export function vehicleUnlockToken(vehicleId: string): string {
  return `veh:${vehicleId}`;
}

/**
 * THE SHOULDER-POCKET CONTRACT (2026-08-11): weapon-frame +Z (behind the grip,
 * toward the shooter) where every "main"-class weapon's stock BUTT must end —
 * the r_* hold clips place the grip hand exactly this far in front of the
 * armpit crook, so a butt ending here rests on the shoulder. Enforced at author
 * time (client/test/build-rifles.mts hard-fails a non-compliant stock) and
 * normalized at load (avatar.seatRifleButt slides an off-spec model along its
 * own axis so its butt still seats).
 */
export const RIFLE_BUTT_SEAT_Z = 0.18;

/**
 * Wardrobe tokens implied by WORN gear (2026-08-10): wearing a piece SHOWS it
 * unless the slot is transmogged, so the appearance clamp must treat the worn
 * kit as backing its own look. Without this, a drop-granted kit (never in the
 * unlocks ledger) is stripped off the wire at join/restyle — the player sees
 * themselves dressed while everyone else sees a bare suit.
 */
export function equippedStyleTokens(equipped: Partial<Record<EquipSlot, string>>): string[] {
  const out: string[] = [];
  for (const id of Object.values(equipped)) {
    const g = id ? ITEMS[id]?.gear : undefined;
    if (g) out.push(gearUnlockToken(g.slot, g.variant));
  }
  return out;
}

/**
 * The WoW rule as a pure function: worn gear SHOWS unless the slot carries a
 * deliberate transmog or an explicitly hidden slot — an expressed preference wins.
 * Shared so the roster portraits, the in-game restore, and anything else that
 * renders "what does this character actually look like" derive it identically.
 * Returns the input by reference when nothing changes.
 */
export function styleWithWornGear(
  style: CharacterStyle,
  equipped: Partial<Record<EquipSlot, string>>,
): CharacterStyle {
  let out = style;
  for (const id of Object.values(equipped)) {
    const g = id ? ITEMS[id]?.gear : undefined;
    if (!g) continue;
    if ((out[g.slot] !== "none" && out[g.slot] !== DEFAULT_STYLE[g.slot]) || out.hiddenGear?.includes(g.slot)) continue;
    if (out === style) out = { ...style };
    out[g.slot] = g.variant;
  }
  return out;
}

/**
 * What every character owns without anything being written to Firestore.
 *
 * COMPUTED, never persisted: the owned set the server checks against is
 * `new Set([...STARTER_UNLOCKS, ...record.unlocks])`. That is what makes gating
 * appearance safe at rollout. Only the Rookie base layers are granted here;
 * Drifter armor still requires ownership. Same idiom as
 * STANDARD_WEAPONS: everyone owns these, they never sit in a bag.
 */
export const STARTER_UNLOCKS: readonly string[] = [
  // Authored base layers are available to every character. This grants no
  // equipment stats, armor, vehicle or other earned appearance.
  "torso:rookie", "legs:rookie", "arms:rookie", "feet:rookie",
];

/** The default active vehicle — what deploys before anything is claimed. */
export const STARTER_VEHICLE = "scout-speeder";

/**
 * The set a character actually owns: the implicit starter grants unioned with the
 * persisted ledger (CharacterRecordV1.unlocks). Takes the raw array rather than the
 * record so this stays in rpg.ts without a cycle through creation.ts.
 *
 * This is THE function every ownership check goes through — appearance clamping,
 * vehicle state validation, and the client's collection push.
 */
export function collectionOwnedSet(unlocks?: readonly string[] | null): Set<string> {
  const out = new Set<string>(STARTER_UNLOCKS);
  if (unlocks) for (const t of unlocks) out.add(canonicalWardrobeToken(t));
  return out;
}

/** Garage vehicle ids from an owned set — "veh:" stripped, unknown chassis dropped. */
export function garageVehicles(owned: ReadonlySet<string>, known: ReadonlySet<string>): string[] {
  const out: string[] = [];
  for (const t of owned) {
    if (!t.startsWith("veh:")) continue;
    const id = t.slice(4);
    if (known.has(id)) out.push(id);
  }
  return out.sort();
}

/** Wardrobe tokens from an owned set — everything that is not a vehicle. */
export function wardrobeTokens(owned: ReadonlySet<string>): string[] {
  return [...owned].filter((t) => !t.startsWith("veh:")).sort();
}

export interface CharacterStyle {
  /** Explicitly hidden cosmetic slots. Equipment stats remain active; absent
   * means legacy default looks still follow equipped gear. */
  hiddenGear?: StylePart[];
  /** Independently authored head, numbered in reference-sheet order. */
  face?: FaceId;
  /** Authored body sculpt; legacy characters default to Balanced. */
  bodyBuild?: BodyBuild;
  /** Optional proportion adjustments layered over the authored build. */
  bodyShape?: BodyShape;
  /** Base body model (BODY_IDS). Missing/invalid → "male". */
  body: BodyId;
  head: string;
  torso: string;
  arms: string;
  legs: string;
  feet: string;
  /** Suit base + accent colors, 0xRRGGBB. */
  suit: number;
  accent: number;
  /** Independent dyes for the authored Rookie base layers (sRGB). */
  shirtColor?: number;
  pantsColor?: number;
  /** Optional v2 appearance fields; absent legacy values validate to defaults. */
  skin?: number;
  hair?: HairId;
  /** Optional v3 appearance colours (2026-07-31): painted over the body's
   *  hair/iris vertices at runtime. Absent legacy values validate to defaults. */
  hairColor?: number;
  eyeColor?: number;
  /** Ki-aura tint (2026-08-13): the flight aura is per-pilot, like hair/eyes. */
  auraColor?: number;
}

// Appearance decks (2026-07-31 colour axes) — shared by the NPC randomizer and
// the character creator so both draw from the same believable palette.
// `skin` MULTIPLIES the baked skin tone; hair/eye REPLACE painted vertices.
// 2026-08-02 (user call): the dark end pulled up — the multiply compounds with the
// baked shading, so the old deep values rendered near-black in world light.
export const SKIN_COLORS: readonly number[] =
  [0xffe0c2, 0xeabc93, 0xd49d75, 0xb88262, 0x9a6952, 0x825c46];
/** Retired deck values (pre-2026-08-02) → their lifted replacements, so persisted
 *  player styles brighten with the deck instead of keeping the near-black multiply. */
export const LEGACY_SKIN_COLORS: Readonly<Record<number, number>> = {
  0xe8b98e: 0xeabc93, 0xc98f68: 0xd49d75, 0xa86f4f: 0xb88262,
  0x7c4c38: 0x9a6952, 0x513126: 0x825c46,
};
export const HAIR_COLORS: readonly number[] = [
  0x17140f, 0x2a2320, 0x4a3421, 0x6b482a, 0x8a5a33, 0xa8783f,
  0xc7a15c, 0x6f7276, 0xd8d5cf, 0x7a3123,
]; // black · soft black · dark brown · brown · chestnut · light brown · blonde · grey · white · auburn
export const EYE_COLORS: readonly number[] =
  [0x3d2f23, 0x5b4632, 0x2f4a5e, 0x3e6b50, 0x6e7a82, 0x8a6b35, 0x4a3b63];
// brown · hazel · blue · green · grey · amber · violet
export const AURA_COLORS: readonly number[] = [
  0xffffff, 0x8fd8ff, 0x2fb8df, 0xffd76a, 0xff8a3d, 0xff4d5e, 0xb06bff, 0x6bff9e,
]; // white · ice · cyan · gold · ember · crimson · violet · jade
export const OUTFIT_COLORS: readonly number[] = [
  0x344c66, 0x252a30, 0x8c7b60, 0x56654c, 0x8a4939, 0x655064, 0xc6bda9, 0x253547,
]; // slate · charcoal · sand · moss · rust · plum · ivory · navy

export const DEFAULT_STYLE: CharacterStyle = {
  body: "male",
  bodyBuild: "balanced",
  head: "none", // bare head: show the male face + hair
  torso: "rookie",
  arms: "rookie",
  legs: "rookie",
  feet: "rookie",
  suit: 0x9aa7b8,
  accent: 0x2fb8df,
  shirtColor: 0x344c66,
  pantsColor: 0x252a30,
  skin: 0xffffff,
  hair: "spiky",
  hairColor: 0x2a2320, // ≈ the baked near-black
  eyeColor: 0x3e6b50,  // ≈ the baked green iris
  auraColor: 0xffffff, // ki aura defaults white
};

/**
 * Validate an untrusted/persisted style. PER-FIELD defaulting (not all-or-nothing): a slot that's
 * missing or invalid falls back to DEFAULT_STYLE, so adding new slots never rejects an old save — old
 * pilots retain their visible pieces through the five-slot migration. Old attachment names
 * map to their combined region; retired variants fall back. Returns null only when there's no
 * object at all (the caller then uses DEFAULT_STYLE).
 */
export function validateStyle(raw: unknown): CharacterStyle | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  // Explicit modern choices win. Otherwise preserve any worn legacy attachment
  // in its new combined region; a retired attachment cannot become a sixth slot.
  const legacy = (...values: unknown[]): unknown => values.find(v => v === "drifter" || v === "rookie") ?? values.find(v => v === "none") ?? values.find(v => v !== undefined);
  const raws: Record<StylePart, unknown> = {
    head: o.head ?? o.helmet,
    torso: o.torso ?? legacy(o.chest === "scout" ? "none" : o.chest, o.back ?? o.pack),
    arms: o.arms ?? legacy(o.hands, o.shoulder),
    legs: o.legs ?? legacy(o.pants, o.waist),
    feet: o.feet ?? o.shoe,
  };
  const pick = (part: StylePart): string => {
    const v = raws[part];
    return typeof v === "string" && STYLE_VARIANTS[part].includes(v) ? v : DEFAULT_STYLE[part];
  };
  const color = (v: unknown, d: number): number =>
    typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 0xffffff ? v : d;
  const out: Record<string, unknown> = {
    // "block" (holographic) retired as a PLAYER body (2026-08-10): legacy saves
    // migrate to the explorer; the id stays in BODY_IDS for dev/NPC use.
    body: o.body === "block" ? "male"
      : typeof o.body === "string" && (BODY_IDS as readonly string[]).includes(o.body) ? o.body : DEFAULT_STYLE.body,
    suit: color(o.suit, DEFAULT_STYLE.suit),
    accent: color(o.accent, DEFAULT_STYLE.accent),
    shirtColor: color(o.shirtColor, DEFAULT_STYLE.shirtColor!),
    pantsColor: color(o.pantsColor, DEFAULT_STYLE.pantsColor!),
    skin: LEGACY_SKIN_COLORS[color(o.skin, DEFAULT_STYLE.skin ?? 0xffffff)]
      ?? color(o.skin, DEFAULT_STYLE.skin ?? 0xffffff),
    hair: validateHairId(o.hair, typeof o.body === "string" ? o.body : "male"),
    hairColor: color(o.hairColor, DEFAULT_STYLE.hairColor ?? 0x2a2320),
    eyeColor: color(o.eyeColor, DEFAULT_STYLE.eyeColor ?? 0x3e6b50),
    auraColor: color(o.auraColor, DEFAULT_STYLE.auraColor ?? 0xffffff),
    bodyShape: validateBodyShape(o.bodyShape),
    bodyBuild: validateBodyBuild(o.bodyBuild),
    face: validateFaceId(o.face, typeof o.body === 'string' ? o.body : 'male'),
  };
  for (const part of STYLE_PARTS) out[part] = pick(part);
  if (Array.isArray(o.hiddenGear)) {
    const requested = new Set(o.hiddenGear.filter((v): v is string => typeof v === "string").map(canonicalStylePart));
    const hidden = STYLE_PARTS.filter(part => requested.has(part) && out[part] === "none");
    if (hidden.length) out.hiddenGear = hidden;
  }
  return out as unknown as CharacterStyle;
}

/**
 * Clamp an appearance to what a character has actually collected (v49).
 *
 * CLAMPS, never rejects — a part whose wardrobe token is missing falls back to the
 * DEFAULT_STYLE value rather than failing the whole style, matching validateStyle's
 * per-field precedent. Colours, body, hair and eyes are not gated: they are creator
 * choices, not collectibles.
 *
 * This is the enforcement point for transmog. Without it `rooms.restyle` will wear
 * anything a hand-edited client sends.
 */
export function clampStyleToUnlocks(
  style: CharacterStyle,
  owned: ReadonlySet<string>,
): CharacterStyle {
  const canonicalOwned = new Set([...owned].map(canonicalWardrobeToken));
  let changed = false;
  const out: CharacterStyle = { ...style };
  for (const part of STYLE_PARTS) {
    const v = style[part];
    // "none" is the bare body — always available, never a collectible.
    if (v === "none" || v === DEFAULT_STYLE[part] || canonicalOwned.has(gearUnlockToken(part, v))) continue;
    out[part] = DEFAULT_STYLE[part];
    changed = true;
  }
  return changed ? out : style;
}

/** Item grade Q1..Q5 at the settlement boundary (v50). Q2 is the ordinary
 *  grade and is never stored on a stack — normalizeItemQuality collapses it to
 *  `undefined`, so pre-grade saves and everyday goods stay unstamped and keep
 *  merging, while the settlement's legacy-medium rule round-trips losslessly. */
export type ItemQuality = 1 | 2 | 3 | 4 | 5;

export function normalizeItemQuality(q: ItemQuality | undefined): ItemQuality | undefined {
  return q === 2 ? undefined : q;
}

export interface ItemStack {
  id: string;
  n: number;
  /** 1 ⇔ this stack is SOULBOUND (v49). Absent = tradeable.
   *  ⚠ Bound and unbound stacks of the same id must never merge, or binding launders
   *  away — every merge/consume site keys on sameStack(), not on `id` alone. */
  b?: 1;
  /** Settlement grade (v50). Absent ⇔ ordinary Q2 (see ItemQuality).
   *  ⚠ Same law as `b`: stacks of different grades must never merge, or a Q5
   *  harvest launders into an ordinary pile the moment it lands in a bag. */
  q?: ItemQuality;
}

/** True if this stack is soulbound and may not change hands. */
export function stackIsBound(s: ItemStack | null | undefined): boolean {
  return s?.b === 1;
}

/** Stack identity for merge/consume: an id match is NOT enough (see ItemStack.b/q). */
function sameStack(a: ItemStack, b: ItemStack): boolean {
  return a.id === b.id && stackIsBound(a) === stackIsBound(b) && a.q === b.q;
}

export interface InventoryState {
  /** Fixed-length slot array (INV_SLOTS), sparse via nulls. */
  slots: (ItemStack | null)[];
  /** Equip points: slot indexes holding weapons, or null. */
  primary: number | null;
  secondary: number | null;
  /** Suit cosmetics land with character customization; schema-ready now. */
  suit: number | null;
  /** The equipped STANDARD-ISSUE weapon (STANDARD_WEAPONS id), or null = unarmed. */
  standard: string | null;
  /**
   * WORN equipment (v49): equip slot → item id. Sparse; an absent key is empty.
   *
   * ⚠ The item exists ONLY here — it is not also in `slots`, which is the whole point
   * (nine bag squares back on a kitted character). validateInventory's drop rule for a
   * bad entry is therefore deliberately narrow: dropping one destroys the item.
   *
   * Firestore-safe: a flat string→string map, no nested arrays.
   */
  equipped: Partial<Record<EquipSlot, string>>;
}

export interface HealthState {
  hp: number;
  max: number;
}

export const DEFAULT_HEALTH: HealthState = { hp: 100, max: 100 };

/** Starter kit. NO weapons: the three combat modes are part of the body (see
 *  shared/src/combat.ts), so there is nothing to carry, equip, or reload. */
export function defaultInventory(): InventoryState {
  const slots: (ItemStack | null)[] = new Array(INV_SLOTS).fill(null);
  slots[0] = { id: "medkit", n: 2 };
  slots[1] = { id: "fuel-cell", n: 3 }; // three jumps before you must mine
  // NO issued weapon (2026-08-10): fists until a rifle DROPS. Z draws whatever
  // is worn in the mainhand slot.
  return { slots, primary: null, secondary: null, suit: null, standard: null, equipped: {} };
}

/**
 * Sanitize the worn-equipment map. Tolerant per-field, like validateStyle.
 *
 * The drop rule is deliberately NARROW — unknown item id, wrong kind, or a gear piece
 * filed under the wrong slot. Worn gear exists nowhere else, so an over-eager rule here
 * destroys the item rather than merely resetting a cosmetic.
 */
function validateEquipped(raw: unknown): Partial<Record<EquipSlot, string>> {
  const out: Partial<Record<EquipSlot, string>> = {};
  if (typeof raw !== "object" || raw === null) return out;
  for (const [oldSlot, value] of Object.entries(raw as Record<string, unknown>).sort(([a], [b]) => Number(isEquipSlot(a)) - Number(isEquipSlot(b)))) {
    const slot = canonicalStylePart(oldSlot) ?? oldSlot;
    if (!isEquipSlot(slot) || typeof value !== "string") continue;
    const def = ITEMS[canonicalItemId(value)];
    if (!def) continue;
    if (slot === "mainhand" || slot === "offhand") {
      // Constructs are body parts, not carried items — they can never be equipped.
      if (def.kind !== "weapon" || def.weapon?.construct) continue;
    } else if (def.kind !== "gear" || def.gear?.slot !== slot) {
      continue;
    }
    out[slot] = def.id;
  }
  return out;
}

/**
 * Validate an untrusted inventory. Returns a sanitized copy, or null if
 * structurally hopeless (callers typically fall back to defaultInventory()).
 */
export function validateInventory(raw: unknown): InventoryState | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.slots) || o.slots.length > INV_SLOTS) return null;
  const slots: (ItemStack | null)[] = new Array(INV_SLOTS).fill(null);
  for (let i = 0; i < o.slots.length; i++) {
    const s = o.slots[i];
    if (s === null || s === undefined) continue;
    if (typeof s !== "object") return null;
    const e = s as Record<string, unknown>;
    const def = typeof e.id === "string" ? ITEMS[canonicalItemId(e.id)] : undefined;
    // An id the catalog no longer knows DROPS the stack — it must not fail the whole
    // inventory, or every save from before the weapons were retired would validate to
    // null and fall back to a fresh starter kit, wiping the player's bags.
    if (!def) continue;
    if (typeof e.n !== "number" || !Number.isInteger(e.n) || e.n < 1) return null;
    // Mode constructs are body parts, not cargo; they can never sit in a slot.
    if (def.weapon?.construct) continue;
    slots[i] = { id: def.id, n: Math.min(e.n, def.stack) };
    // Soulbound flag (v49). A stack bound by a retired rule stays bound: binding is a
    // property the player earned by wearing it, and unbinding would launder it.
    if (e.b === 1 || e.b === true) slots[i]!.b = 1;
    // Grade stamp (v50). Q2 normalizes to absent; junk drops the stamp, not the stack.
    if (e.q === 1 || e.q === 3 || e.q === 4 || e.q === 5) slots[i]!.q = e.q as ItemQuality;
  }
  const equip = (v: unknown): number | null => {
    if (typeof v !== "number" || !Number.isInteger(v) || v < 0 || v >= INV_SLOTS) return null;
    return slots[v]?.id && ITEMS[slots[v]!.id].kind === "weapon" ? v : null;
  };
  const primary = equip(o.primary);
  let secondary = equip(o.secondary);
  if (secondary !== null && secondary === primary) secondary = null;
  // RETIRED FIELD (2026-08-10): standard-issue is gone — weapons are drops worn
  // in the mainhand slot. Every save migrates to null; the free rifle with it.
  const standard = null;
  // v49: a save from before equip slots existed simply has nothing worn.
  const equipped = validateEquipped(o.equipped);
  return { slots, primary, secondary, suit: null, standard, equipped };
}

/** Health clamp for untrusted persisted values. */
export function validateHealth(raw: unknown): HealthState {
  const o = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const max =
    typeof o.max === "number" && Number.isFinite(o.max)
      ? Math.min(500, Math.max(1, o.max))
      : DEFAULT_HEALTH.max;
  const hp =
    typeof o.hp === "number" && Number.isFinite(o.hp)
      ? Math.min(max, Math.max(0, o.hp))
      : max;
  return { hp, max };
}

// --- Inventory operations (pure helpers; callers persist/refresh) ---

/** Add `n` of `id`, filling stacks then empty slots. Returns the leftover.
 *  `bound` overrides the def's binding rule (unequipping returns a bound stack);
 *  omitted, a `bind: "pickup"` item binds on the way in. */
export function addItem(inv: InventoryState, id: string, n: number, bound?: boolean,
  quality?: ItemQuality): number {
  const def = ITEMS[id];
  if (!def || n <= 0) return n;
  const bind = bound ?? def.bind === "pickup";
  const q = normalizeItemQuality(quality);
  let left = n;
  for (const s of inv.slots) {
    if (left === 0) break;
    // Only merge into a stack of matching binding AND grade — see ItemStack.b/q.
    if (s?.id === id && stackIsBound(s) === bind && s.q === q && s.n < def.stack) {
      const take = Math.min(left, def.stack - s.n);
      s.n += take;
      left -= take;
    }
  }
  for (let i = 0; i < inv.slots.length && left > 0; i++) {
    if (inv.slots[i] === null) {
      const take = Math.min(left, def.stack);
      inv.slots[i] = { id, n: take, ...(bind ? { b: 1 as const } : {}), ...(q ? { q } : {}) };
      left -= take;
    }
  }
  return left;
}

/** Spend `n` of `id` from UNBOUND stacks only, reporting the exact grades
 *  removed (absent q = ordinary Q2). Null and no change when short. The
 *  settlement boundary uses this so grades never flatten on deposit. */
export function consumeUnboundGraded(inv: InventoryState, id: string,
  n: number): { n: number; q?: ItemQuality }[] | null {
  if (countUnbound(inv, id) < n) return null;
  let left = n;
  const taken = new Map<number, number>();
  for (let i = 0; i < inv.slots.length && left > 0; i++) {
    const s = inv.slots[i];
    if (!s || s.id !== id || stackIsBound(s)) continue;
    const take = Math.min(left, s.n);
    s.n -= take;
    left -= take;
    taken.set(s.q ?? 0, (taken.get(s.q ?? 0) ?? 0) + take);
    if (s.n === 0) {
      inv.slots[i] = null;
      if (inv.primary === i) inv.primary = null;
      if (inv.secondary === i) inv.secondary = null;
    }
  }
  return [...taken].sort(([a], [b]) => a - b)
    .map(([q, count]) => q === 0 ? { n: count } : { n: count, q: q as ItemQuality });
}

/** Total rounds of an ammo type across the inventory. */
export function ammoCount(inv: InventoryState, type: AmmoType): number {
  let n = 0;
  for (const s of inv.slots) {
    if (s && ITEMS[s.id].ammo === type) n += s.n;
  }
  return n;
}

/** Consume `n` rounds of an ammo type. False (and no change) if short. */
export function consumeAmmo(inv: InventoryState, type: AmmoType, n: number): boolean {
  if (ammoCount(inv, type) < n) return false;
  let left = n;
  for (let i = 0; i < inv.slots.length && left > 0; i++) {
    const s = inv.slots[i];
    if (!s || ITEMS[s.id].ammo !== type) continue;
    const take = Math.min(left, s.n);
    s.n -= take;
    left -= take;
    if (s.n === 0) inv.slots[i] = null;
  }
  return true;
}

/** Decrement a slot by `n`, clearing it (and any equip pointer) at zero. */
export function consumeAt(inv: InventoryState, slot: number, n = 1): void {
  const s = inv.slots[slot];
  if (!s) return;
  s.n -= n;
  if (s.n <= 0) {
    inv.slots[slot] = null;
    if (inv.primary === slot) inv.primary = null;
    if (inv.secondary === slot) inv.secondary = null;
  }
}

/** Move/merge/swap between two slots (the inventory-grid interaction). */
export function moveItem(inv: InventoryState, from: number, to: number): void {
  if (from === to) return;
  const a = inv.slots[from];
  const b = inv.slots[to];
  if (!a) return;
  if (b && sameStack(a, b)) {
    // Merge; if the source empties, equip pointers follow into the target.
    // Binding must match: merging a bound stack into a free one would launder it.
    const def = ITEMS[a.id];
    const take = Math.min(a.n, def.stack - b.n);
    b.n += take;
    a.n -= take;
    if (a.n === 0) {
      inv.slots[from] = null;
      if (inv.primary === from) inv.primary = to;
      if (inv.secondary === from) inv.secondary = to;
    }
  } else {
    // Swap; equip pointers follow their items.
    inv.slots[from] = b;
    inv.slots[to] = a;
    const follow = (v: number | null): number | null =>
      v === from ? to : v === to ? from : v;
    inv.primary = follow(inv.primary);
    inv.secondary = follow(inv.secondary);
  }
}

// --- Equip slots (v49): moving items between the bag and the body ---

/** Capacity for `n` of `id` without displacing anything (binding-aware). */
export function hasRoomFor(inv: InventoryState, id: string, n = 1, bound?: boolean): boolean {
  const def = ITEMS[id];
  if (!def) return false;
  const bind = bound ?? def.bind === "pickup";
  let room = 0;
  for (const s of inv.slots) {
    if (s === null) room += def.stack;
    else if (s.id === id && stackIsBound(s) === bind) room += def.stack - s.n;
    if (room >= n) return true;
  }
  return room >= n;
}

/** Which equip slot an item occupies, or null if it is not equippable. */
export function equipSlotFor(def: ItemDef): EquipSlot | null {
  if (def.kind === "gear") return def.gear?.slot ?? null;
  // Constructs are body parts, not carried items.
  if (def.kind === "weapon" && !def.weapon?.construct) return "mainhand";
  return null;
}

/**
 * Wear the item at bag slot `at`. The stack LEAVES the bag — that is the point.
 * Anything already in the target equip slot swaps back into `at` (bound, since it
 * has been worn), so the player's square is never reassigned behind their back.
 *
 * Returns the equip slot filled, or null if the item is not equippable.
 */
export function equipFromSlot(inv: InventoryState, at: number): EquipSlot | null {
  const stack = inv.slots[at];
  if (!stack) return null;
  const def = ITEMS[stack.id];
  if (!def) return null;
  const slot = equipSlotFor(def);
  if (!slot) return null;
  const displaced = inv.equipped[slot];
  inv.slots[at] = displaced ? { id: displaced, n: 1, b: 1 } : null;
  if (inv.primary === at) inv.primary = null;
  if (inv.secondary === at) inv.secondary = null;
  inv.equipped[slot] = def.id;
  // A looted mainhand supersedes the standard-issue selector, and vice versa
  // (equippedWeapon() resolves mainhand first) — keep exactly one of them set.
  if (slot === "mainhand") inv.standard = null;
  return slot;
}

/**
 * Take off whatever is in `slot`, returning it to the bag SOULBOUND (it has been worn).
 * False — with no mutation — when the bag is full, so the caller can toast BAG FULL.
 */
export function unequipSlot(inv: InventoryState, slot: EquipSlot): boolean {
  const id = inv.equipped[slot];
  if (!id) return false;
  if (!hasRoomFor(inv, id, 1, true)) return false; // checked BEFORE mutating
  addItem(inv, id, 1, true);
  delete inv.equipped[slot];
  return true;
}
